import { ArrowLeft, ArrowRight, Loader2, Mail } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ProviderMark,
  type OAuthProvider,
} from "@/components/auth/ProviderMark";
import { cn } from "@/engine/support/classNames";

/** Every word the sign-in says; the application passes its catalogue. */
export interface CampSignInCopy {
  /** A provider button, e.g. "Continue with Google". */
  provider: (name: string) => string;
  emailDivider: string;
  emailLabel: string;
  emailPlaceholder: string;
  sendLink: string;
  sentTitle: string;
  sentBody: (email: string) => string;
  openLink: string;
  otherAddress: string;
  back: string;
  skip: string;
  /** The tag on the provider used last time, e.g. "Last used". */
  lastUsed: string;
}

export interface CampSignInProps {
  copy: CampSignInCopy;
  providers?: readonly OAuthProvider[] | undefined;
  /** `sent` once the link has gone out; the screen offers to open it. */
  phase: "choose" | "sent";
  /** What is being waited on, if anything; every way in waits with it. */
  pending?: OAuthProvider | "email" | null | undefined;
  email: string;
  /** Why the last attempt failed, said above the ways in. */
  error?: string | undefined;
  onEmailChange: (email: string) => void;
  onEmailSubmit: () => void;
  onProvider: (provider: OAuthProvider) => void;
  /** Where the link from the email leads, when the screen can follow it itself. */
  onOpenLink?: (() => void) | undefined;
  onOtherAddress: () => void;
  onBack?: (() => void) | undefined;
  /** A guest may always carry on without an account. */
  onSkip?: (() => void) | undefined;
  /** The way in that worked last time; it is offered first, and says so. */
  lastUsed?: OAuthProvider | "email" | null | undefined;
  /** The application's handles on the sign-in, for its browser tests. */
  testIds?:
    | Partial<
        Record<
          | "root"
          | "google"
          | "apple"
          | "microsoft"
          | "email"
          | "sendLink"
          | "sent"
          | "error",
          string | undefined
        >
      >
    | undefined;
  className?: string | undefined;
}

const PROVIDER_NAMES: Record<OAuthProvider, string> = {
  google: "Google",
  apple: "Apple",
  microsoft: "Microsoft",
};

type SignInTestIds = NonNullable<CampSignInProps["testIds"]>;

function SentCard({
  copy,
  email,
  onOpenLink,
  onOtherAddress,
  testId,
}: Pick<CampSignInProps, "copy" | "email" | "onOpenLink" | "onOtherAddress"> & {
  testId?: string | undefined;
}) {
  return (
    <div
      data-testid={testId}
      className="space-y-4 rounded-2xl border border-primary/35 bg-primary/8 p-5"
    >
      <Mail className="size-6 text-primary" aria-hidden="true" />
      <div className="space-y-1">
        <p className="font-semibold">{copy.sentTitle}</p>
        <p className="text-sm text-muted-foreground">{copy.sentBody(email)}</p>
      </div>
      {onOpenLink ? (
        <Button className="w-full" size="lg" onClick={onOpenLink}>
          {copy.openLink}
        </Button>
      ) : null}
      <Button className="w-full" variant="ghost" onClick={onOtherAddress}>
        {copy.otherAddress}
      </Button>
    </div>
  );
}

function ProviderButtons({
  providers,
  lastUsed,
  pending,
  copy,
  onProvider,
  testIds,
}: {
  providers: readonly OAuthProvider[];
  lastUsed: CampSignInProps["lastUsed"];
  pending: CampSignInProps["pending"];
  copy: CampSignInCopy;
  onProvider: (provider: OAuthProvider) => void;
  testIds: SignInTestIds;
}) {
  // The way in that worked last time comes first, so it is found rather than looked for.
  const ordered =
    lastUsed && lastUsed !== "email" && providers.includes(lastUsed)
      ? [lastUsed, ...providers.filter((provider) => provider !== lastUsed)]
      : providers;
  return (
    <div className="space-y-2.5">
      {ordered.map((provider) => (
        <Button
          key={provider}
          variant="outline"
          size="lg"
          className={cn(
            "relative w-full",
            lastUsed === provider && "border-primary/50",
          )}
          disabled={pending != null}
          data-testid={testIds[provider]}
          onClick={() => onProvider(provider)}
        >
          {pending === provider ? (
            <Loader2 className="animate-spin" aria-hidden="true" />
          ) : (
            <ProviderMark provider={provider} />
          )}
          {copy.provider(PROVIDER_NAMES[provider])}
          {lastUsed === provider ? (
            <span className="absolute right-3 rounded-md bg-primary/10 px-1.5 py-0.5 text-xs text-primary">
              {copy.lastUsed}
            </span>
          ) : null}
        </Button>
      ))}
    </div>
  );
}

function EmailForm({
  copy,
  email,
  pending,
  onEmailChange,
  onEmailSubmit,
  testIds,
}: Pick<
  CampSignInProps,
  "copy" | "email" | "pending" | "onEmailChange" | "onEmailSubmit"
> & {
  testIds: SignInTestIds;
}) {
  const busy = pending != null;
  return (
    <form
      className="space-y-2.5"
      onSubmit={(event) => {
        event.preventDefault();
        onEmailSubmit();
      }}
    >
      <Input
        type="email"
        autoComplete="email"
        value={email}
        onChange={(event) => onEmailChange(event.target.value)}
        placeholder={copy.emailPlaceholder}
        aria-label={copy.emailLabel}
        data-testid={testIds.email}
        disabled={busy}
      />
      <Button
        type="submit"
        size="lg"
        className="w-full"
        disabled={busy || !email.includes("@")}
        data-testid={testIds.sendLink}
      >
        {pending === "email" ? (
          <Loader2 className="animate-spin" aria-hidden="true" />
        ) : (
          <Mail aria-hidden="true" />
        )}
        {copy.sendLink}
      </Button>
    </form>
  );
}

function BackOrSkip({
  copy,
  busy,
  onBack,
  onSkip,
}: Pick<CampSignInProps, "copy" | "onBack" | "onSkip"> & { busy: boolean }) {
  if (!onBack && !onSkip) return null;
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <Button
        variant="ghost"
        size="sm"
        onClick={onBack}
        disabled={busy}
        className={cn(!onBack && "invisible")}
      >
        <ArrowLeft aria-hidden="true" /> {copy.back}
      </Button>
      {onSkip ? (
        <Button variant="link" size="sm" onClick={onSkip} disabled={busy}>
          {copy.skip}
          <ArrowRight aria-hidden="true" />
        </Button>
      ) : null}
    </div>
  );
}

/**
 * Every way in: the providers, the email link and the way to carry on without
 * an account, inside a surface that already says why an account is wanted.
 */
export function CampSignIn({
  copy,
  providers = ["google", "apple", "microsoft"],
  phase,
  pending = null,
  email,
  error,
  onEmailChange,
  onEmailSubmit,
  onProvider,
  onOpenLink,
  onOtherAddress,
  onBack,
  onSkip,
  lastUsed = null,
  testIds = {},
  className,
}: CampSignInProps) {
  const busy = pending !== null;
  return (
    <div className={cn("space-y-4", className)} data-testid={testIds.root}>
      {error && phase !== "sent" ? (
        <p
          role="alert"
          data-testid={testIds.error}
          className="rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
        >
          {error}
        </p>
      ) : null}
      {phase === "sent" ? (
        <SentCard
          copy={copy}
          email={email}
          onOpenLink={onOpenLink}
          onOtherAddress={onOtherAddress}
          testId={testIds.sent}
        />
      ) : (
        <>
          <ProviderButtons
            providers={providers}
            lastUsed={lastUsed}
            pending={pending}
            copy={copy}
            onProvider={onProvider}
            testIds={testIds}
          />
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span className="h-px flex-1 bg-border" />
            {copy.emailDivider}
            <span className="h-px flex-1 bg-border" />
          </div>
          <EmailForm
            copy={copy}
            email={email}
            pending={pending}
            onEmailChange={onEmailChange}
            onEmailSubmit={onEmailSubmit}
            testIds={testIds}
          />
        </>
      )}
      <BackOrSkip copy={copy} busy={busy} onBack={onBack} onSkip={onSkip} />
    </div>
  );
}
